import { computed, inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { findIana } from 'windows-iana';
// eslint-disable-next-line @nx/enforce-module-boundaries -- DateTimeService needs SettingsStore for timezone; cycle via settings stores using prop-state from application
import { SettingsStore } from '@settings/data-access';

/**
 * Service responsible for handling date/time conversions between UTC and user's local timezone.
 * Automatically converts Windows Time Zone IDs to IANA timezone identifiers.
 */
@Injectable({ providedIn: 'root' })
export class DateTimeService {
  private readonly settingsStore = inject(SettingsStore);
  private readonly ownerSettings = computed(() => this.settingsStore.ownerSettings());

  /**
   * The machine's own IANA zone, read once — it cannot change mid-session.
   *
   * Used as the fallback of {@link getUserZone}, never as a preference: whenever
   * the account states a timezone, that wins. `'UTC'` is only the last resort for
   * an environment where `Intl` answers with nothing — or com um fuso que ele
   * próprio recusa; ver {@link resolveSystemZone}.
   */
  private readonly systemZone: string = resolveSystemZone();

  /**
   * Gets the user's timezone in IANA format (e.g., "America/Sao_Paulo").
   * Converts from Windows Time Zone ID format if necessary.
   *
   * Falls back to the machine's zone — NOT to `'UTC'`. This value feeds
   * `Settings.defaultZone` app-wide (see `LUXON_ZONE_INITIALIZER`), so a `'UTC'`
   * fallback would put every bare `DateTime.now()` on UTC wall time for accounts
   * with no timezone configured, and for the whole boot window before
   * `ownerSettings` resolves — reading hours ahead of the account's real clock
   * for a UTC-negative zone (3h in Brazil). The machine zone keeps that window
   * behaving exactly as Luxon does on its own.
   *
   * @returns IANA timezone string; the machine's zone as fallback
   */
  public getUserZone(): string {
    return this.userZone();
  }

  /**
   * O fuso resolvido, memoizado até `ownerSettings` mudar.
   *
   * `computed` e não um método puro porque isto está em caminhos quentes — o
   * `DateInterceptor` chama-o por pedido e o `toUtcObject` por campo — e o corpo
   * faz um `findIana` mais uma validação de fuso, ambos caros de repetir para um
   * valor que só muda quando a conta muda.
   */
  private readonly userZone = computed(() => this.resolveUserZone());

  private resolveUserZone(): string {
    const windowsTimezone = this.ownerSettings()?.Timezone;
    if (!windowsTimezone) {
      return this.systemZone;
    }

    try {
      // O primeiro alias nem sempre é o mais suportado: `findIana('UTC-11')`
      // devolve `['Etc/GMT+11', 'Pacific/Pago_Pago', …]`, e num runtime de ICU
      // reduzido só o segundo existe. Percorrer a lista aproveita-o em vez de
      // deitar fora o fuso da conta por causa do primeiro.
      const candidate =
        findIana(windowsTimezone)?.find(isSupportedZone) ?? windowsTimezone;

      return isSupportedZone(candidate) ? candidate : this.systemZone;
    } catch {
      return this.systemZone;
    }
  }

  /**
   * Traz para a hora de parede da conta todas as propriedades que carregam um
   * instante UTC — as que terminam em `Utc` e as declaradas em
   * `extraUtcDateFields`. Percorre objetos aninhados e arrays.
   *
   * @param obj - objeto a converter
   * @param extraUtcDateFields - nomes que também são instantes apesar de não
   *        terminarem em `Utc` (`SendAt`, `ts`, ...). A comparação ignora
   *        maiúsculas/minúsculas, portanto `'SendAt'` cobre o `sendAt` que os
   *        hubs mandam em camelCase.
   * @returns novo objeto com as datas convertidas
   */
  public toLocalObject<T>(obj: T, extraUtcDateFields?: readonly string[]): T {
    return this.convertObject(
      obj,
      this.toFieldSet(extraUtcDateFields),
      (value) => this.toLocalInstant(value)
    );
  }

  /**
   * O inverso de {@link toLocalObject}: lê cada valor como hora de parede da
   * conta e devolve o instante UTC, na forma que o backend espera (sufixo `Z`,
   * fração de 6 dígitos).
   *
   * @param obj - objeto a converter
   * @param extraUtcDateFields - ver {@link toLocalObject}
   * @returns novo objeto com as datas convertidas
   */
  public toUtcObject<T>(obj: T, extraUtcDateFields?: readonly string[]): T {
    return this.convertObject(
      obj,
      this.toFieldSet(extraUtcDateFields),
      (value) => this.toUtcInstant(value)
    );
  }

  /**
   * Caminhada única partilhada por {@link toLocalObject} e {@link toUtcObject}:
   * a única diferença entre os dois é o `convert` que recebem.
   *
   * `FormData`/`Blob` saem intocados (o corpo binário não é para reescrever) e
   * um `Date` no meio da árvore também: só é convertido quando está debaixo de
   * uma chave de data, onde o `convert` sabe o que fazer com ele.
   */
  private convertObject<T>(
    obj: T,
    fields: ReadonlySet<string>,
    convert: (value: unknown) => unknown
  ): T {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) =>
        this.convertObject(item, fields, convert)
      ) as unknown as T;
    }

    if (obj instanceof Date || obj instanceof FormData || obj instanceof Blob) {
      return obj;
    }

    const result = {} as T;

    for (const [key, value] of Object.entries(obj)) {
      const converted = this.isDateField(key, fields) ? convert(value) : value;

      // `converted === value` quando não havia instante nenhum para converter —
      // ou porque a chave não é de data, ou porque o valor não é um. Se for um
      // objeto, continua a caminhada por dentro dele.
      (result as never)[key as never] =
        converted === value && typeof value === 'object' && value !== null
          ? (this.convertObject(value, fields, convert) as never)
          : (converted as never);
    }

    return result;
  }

  /** Sufixo `Utc` — a convenção — ou uma exceção declarada pelo chamador. */
  private isDateField(key: string, fields: ReadonlySet<string>): boolean {
    return key.endsWith('Utc') || fields.has(key.toLowerCase());
  }

  private toFieldSet(fields: readonly string[] | undefined): ReadonlySet<string> {
    return new Set((fields ?? []).map((field) => field.toLowerCase()));
  }

  /**
   * Lê o valor como instante UTC e devolve-o em hora de parede da conta, na
   * mesma forma que `toISO()` produz em todo o resto da app desde o
   * `LUXON_ISO_INITIALIZER`: hora de parede com o `Z` decorativo.
   *
   * O sufixo NÃO é removido de propósito. Enquanto o era, esta fronteira era o
   * único sítio a fugir à convenção, e existiam duas formas para o mesmo valor
   * — o que partia qualquer comparação de string entre um valor vindo daqui e
   * um produzido por `toISO()` (foi o que escondeu a chave do separador de dia
   * na grelha de movimentos do tesouro). Quem lê o valor não é afetado: tanto
   * `getDateTime` (via `cleanDateStr`) como {@link toInstant} (via
   * {@link cleanIso}) descartam o sufixo antes de o interpretar.
   */
  private toLocalInstant(value: unknown): unknown {
    const utc = this.toInstant(value, 'utc');
    if (!utc) {
      return value;
    }

    return utc.setZone(this.getUserZone()).toISO() ?? value;
  }

  /**
   * Lê o valor como hora de parede da conta e devolve o instante UTC com o
   * sufixo `Z` e a fração a 6 dígitos, que é o que o backend espera.
   */
  private toUtcInstant(value: unknown): unknown {
    const local = this.toInstant(value, this.getUserZone());
    if (!local) {
      return value;
    }

    const iso = local.toUTC().toISO();
    return iso ? this.padMicroseconds(iso) : value;
  }

  /**
   * Interpreta um valor como instante. `zone` diz em que fuso ler a hora de
   * parede de uma STRING — um `Date`, um epoch ou um `DateTime` já são instantes
   * absolutos e não têm hora de parede para reinterpretar.
   *
   * O `Z`/offset de uma string é decoração desde o patch de `toISO` (ver
   * `LUXON_ISO_INITIALIZER`), portanto é removido antes de ler: quem manda no
   * fuso é o `zone`, não o sufixo.
   *
   * Devolve `null` — ou seja, "não há nada para converter" — para vazios, para o
   * que não parseia e para o que não é um instante: uma data sem hora
   * (`2026-08-17`) ou uma hora sem data (`14:30`) não têm ponto no tempo, e
   * convertê-las só deslocaria o dia.
   */
  private toInstant(value: unknown, zone: string): DateTime | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    let dateTime: DateTime | null = null;

    if (typeof value === 'string') {
      if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value.trim())) {
        return null;
      }
      dateTime = DateTime.fromISO(this.cleanIso(value.trim()), { zone });
    } else if (value instanceof Date) {
      dateTime = DateTime.fromJSDate(value);
    } else if (typeof value === 'number') {
      dateTime = DateTime.fromMillis(value);
    } else if (value instanceof DateTime) {
      dateTime = value;
    }

    return dateTime?.isValid ? dateTime : null;
  }

  /**
   * Descarta o sufixo `Z`/offset e trunca a fração a 3 dígitos, para o valor
   * poder ser LIDO sem que o sufixo mande no fuso — ver {@link toInstant}, o
   * seu único chamador. Não é a forma de saída de nada: o que sai desta classe
   * leva o `Z` decorativo, como todo o resto da app.
   *
   * Descartar TODO o offset — inclusive um `+05:30` genuíno, e não só o `Z`
   * decorativo — é decisão deliberada, não descuido. Quem manda no fuso é o
   * parâmetro `zone` do {@link toInstant}, ponto: uma única regra para ler
   * qualquer string, em vez de duas que dependem de o backend ter carimbado ou
   * não o sufixo naquele campo. O preço é conhecido: se algum endpoint passar a
   * devolver um `DateTimeOffset` em hora de servidor com offset não-zero, o
   * valor é lido pelo `zone` e não pelo offset. Nesse dia a correção é declarar
   * o campo, não reintroduzir a leitura do sufixo aqui.
   */
  private cleanIso(value: string): string {
    return value
      .replace(/Z$|[+-]\d{2}:\d{2}$/, '')
      .replace(/(\.\d{3})\d+/, '$1');
  }

  /**
   * Pads the fraction to exactly 6 digits (microseconds), the shape the backend
   * expects on the way in.
   *
   * `2026-01-02T19:24:51.076Z` -> `2026-01-02T19:24:51.076000Z`
   * `2026-01-02T19:24:51Z`     -> `2026-01-02T19:24:51.000000Z`
   *
   * `.999` é a única fração que não é preenchida com zeros — fica `.999999`.
   */
  private padMicroseconds(iso: string): string {
    const fraction = iso.match(/\.(\d+)(Z?)$/);
    if (!fraction) {
      return iso.replace(/Z$/, '.000000Z');
    }

    const digits = fraction[1];
    const zSuffix = fraction[2] || 'Z';
    return digits === '999'
      ? iso.replace(/\.\d+Z?$/, '.999999' + zSuffix)
      : iso.replace(/\.\d+Z?$/, '.' + digits.padEnd(6, '0') + zSuffix);
  }
}

/**
 * Se o `Intl` reconhece este fuso — **sem ler o relógio**.
 *
 * O `IANAZone.isValidZone` do Luxon não serve aqui, por muito que o nome
 * prometa: implementa o teste como `new Intl.DateTimeFormat(…).format()` SEM
 * argumento, e o `format` sem argumento formata o instante atual. Numa estação
 * cujo relógio devolve um instante impossível — o cenário todo desta correção —
 * esse `format` lança, o `catch` do Luxon engole, e a função responde `false`
 * para QUALQUER fuso, incluindo os válidos. Usá-la aqui punha a app a descartar
 * o fuso da conta exatamente na máquina em que isso mais custa.
 *
 * O construtor, esse, valida o `timeZone` e lança `RangeError` para o que não
 * conhece sem nunca tocar na hora; o `resolvedOptions()` só lê a configuração
 * resolvida. É esse o teste — e cobre o `'Etc/Unknown'` com que o Chromium
 * responde quando o ICU não consegue mapear o fuso do Windows.
 */
function isSupportedZone(zone: string): boolean {
  if (!zone) {
    return false;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions();
    return true;
  } catch {
    return false;
  }
}

/**
 * O fuso do SO, validado.
 *
 * O `|| 'UTC'` sozinho não chega: `'Etc/Unknown'` é uma string *truthy* que
 * passa incólume por esse fallback e que tanto o Luxon como o `Intl` rejeitam.
 * Como este valor alimenta o `Settings.defaultZone` de toda a app pelo
 * `LUXON_ZONE_INITIALIZER`, deixá-la passar tornaria inválido cada
 * `DateTime.now()` da sessão.
 *
 * O `'UTC'` continua a ser só o último recurso — ver a nota do
 * {@link DateTimeService.getUserZone} sobre porque cair para UTC desloca a app
 * inteira. É por isso que o teste tem de ser {@link isSupportedZone} e não o do
 * Luxon: um teste que reprove fusos válidos transforma este fallback, que devia
 * ser raríssimo, no caminho normal de uma estação com o relógio partido.
 */
function resolveSystemZone(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isSupportedZone(zone) ? zone : 'UTC';
}
